import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ThumbsUp, MessageCircle, Flame, BadgeCheck, Share2, TicketPercent } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import { API_BASE } from '../config/apiBase';
import useImpressao from '../inteligencia/useImpressao';
import { registrar, registrarCompartilhamento } from '../inteligencia/coleta';
import { REL_AMAZON, linkAmazonDireto, registrarCliqueAmazon } from '../utils/amazonSitestripe';
import { precoAnteriorExibivel } from '../utils/precoAnterior';

/**
 * Card de oferta.
 *
 * Três formas, mesmo conteúdo:
 *   - padrão: linha horizontal no mobile e card vertical a partir de `sm`;
 *   - `variant="tile"`: card vertical em qualquer largura, para os trilhos
 *     horizontais da Home;
 *   - `compact`: miniatura de barra lateral.
 *
 * O card leva para a página da oferta, nunca direto para a loja. A saída
 * monetizada continua sendo decidida lá, pelo redirector.
 *
 * `exposicao` ({ surface, position, section, searchId }): onde o card aparece. Com a
 * coleta da inteligência ligada, mede impressão (>= 50% visível por 500 ms) e manda a
 * superfície e a posição junto do clique. Sem a prop, ou com a coleta desligada, nada muda.
 */
export const DealCard = ({ deal, compact = false, variant = 'default', exposicao = null }) => {
    const {
        deal_id,
        title,
        image_url,
        current_price,
        discount_percent,
        store_name,
        author_name,
        author_picture,
        likes,
        comments_count,
        is_hot,
        created_at,
        coupon_code,
        expiration_status,
    } = deal;

    const refImpressao = useImpressao(deal, exposicao);

    const trackDetailClick = () => {
        registrar('detail_click', {
            deal_id, surface: exposicao?.surface || '', position: exposicao?.position, section: exposicao?.section || '',
            search_id: exposicao?.searchId || '', marketplace: store_name || '',
        });
        fetch(`${API_BASE}/api/track/event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_type: 'deal_detail_click', deal_id, category: deal.category_id || deal.category || '', source: 'web' }),
        }).catch(() => {});
    };

    const formatPrice = (price) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

    const timeAgo = created_at ? formatDistanceToNow(new Date(created_at), { addSuffix: false, locale: ptBR }) : '';

    const [imgError, setImgError] = useState(false);
    const fallbackImg = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" fill="none"><rect width="120" height="120" rx="12" fill="#E7ECF6"/><text x="50%" y="54%" text-anchor="middle" fill="#7B8AA6" font-size="13" font-family="sans-serif">Sem imagem</text></svg>');
    const imgSrc = imgError || !image_url ? fallbackImg : image_url;

    // Marketplaces oficiais ganham selo de verificação.
    const VERIFIED_STORES = ['mercado livre', 'mercadolivre', 'amazon', 'shopee', 'aliexpress', 'magalu', 'magazine luiza', 'magazineluiza', 'americanas', 'casas bahia', 'submarino', 'samsung'];
    const isVerifiedStore = store_name && VERIFIED_STORES.some((s) => store_name.toLowerCase().includes(s));

    // Oferta Amazon manual (SiteStripe): sem preço de fonte oficial. O card omite preço, preço antigo e
    // desconto (nada de "R$ 0,00" ou "0% OFF") e diz onde ver a oferta. Os outros deals não mudam.
    const semPreco = deal.price_mode === 'sem_preco';
    const prime = semPreco && deal.requires_prime === true;

    // Badge e preço antigo são independentes, como eram antes: há deal com
    // desconto informado e sem preço original, e o contrário. Na oferta manual os dois já
    // são falsos: ela não tem desconto nem preço antigo (a API recusa e a vitrine a esconde se tiver).
    const temDesconto = discount_percent > 0;
    // Shopee: o desconto é a taxa oficial e o preço anterior derivado nunca é riscado (utils/precoAnterior).
    const precoAntigo = precoAnteriorExibivel(deal);
    const temPrecoAntigo = precoAntigo !== null;

    // Com `superficie` (cards cuja raiz não é link): <a href> DIRETO para o link oficial do SiteStripe, acima do
    // link esticado do card, como o link da loja. Nunca /r/ nem /api/go/deal; o clique só é contado por beacon.
    // Sem `superficie` (card que já é um link para a página da oferta): só o rótulo, sem <a> dentro de <a>.
    const linkAmazon = linkAmazonDireto(deal);
    const semPrecoCta = (tamanho, superficie) => (superficie && linkAmazon ? (
        <a href={linkAmazon} target="_blank" rel={REL_AMAZON} onClick={() => registrarCliqueAmazon(deal_id, superficie)}
            className={`${tamanho} font-bold relative z-10 hover:underline`} style={{ color: 'var(--sv-action)' }}
            data-testid={`deal-amazon-direto-${deal_id}`}>
            Ver oferta na Amazon
        </a>
    ) : (
        <span className={`${tamanho} font-bold`} style={{ color: 'var(--sv-action)' }} data-testid={`deal-sem-preco-${deal_id}`}>
            Ver oferta na Amazon
        </span>
    ));
    const seloPrime = prime && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0" style={{ background: 'var(--status-warning-bg)', color: 'var(--sv-warning)' }}
            data-testid={`deal-prime-${deal_id}`}>Membros Prime</span>
    );

    const handleShare = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const url = `${window.location.origin}/oferta/${deal_id}`;
        const text = `${title}\n${url}`;
        const superficie = exposicao?.surface || '';
        if (navigator.share) {
            registrarCompartilhamento(deal_id, 'native_share', superficie, navigator.share({ title, text, url }))?.catch(() => {});
        } else {
            // Sem share nativo: copia o link e abre o WhatsApp. A intenção é "whatsapp"; o
            // único passo que o navegador confirma é a cópia.
            registrar('share_intent', { deal_id, destination: 'whatsapp', surface: superficie });
            const copia = navigator.clipboard?.writeText(url);
            if (copia) registrarCompartilhamento(deal_id, 'copy_link', superficie, copia).catch(() => {});
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
        }
    };

    const selosEstado = (
        <>
            {expiration_status === 'expired' && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(255,90,112,0.16)', color: 'var(--sv-discount)' }}>Expirou</span>
            )}
            {expiration_status === 'possibly_expired' && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ background: 'var(--status-warning-bg)', color: 'var(--sv-warning)' }}>Verificar</span>
            )}
        </>
    );

    const loja = (
        <span className="flex items-center gap-1 min-w-0 text-[12px] font-semibold" style={{ color: 'var(--muted)' }}>
            <span className="truncate">{store_name}</span>
            {isVerifiedStore && <BadgeCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--gold-2)' }} aria-label="Loja verificada" />}
        </span>
    );

    const estatisticas = (tamanho = 'w-3.5 h-3.5') => (
        <span className="flex items-center gap-2.5 text-[12px] sv-num" style={{ color: 'var(--muted-2)' }}>
            <span className="flex items-center gap-1" aria-label={`${likes || 0} curtidas`}><ThumbsUp className={tamanho} />{likes || 0}</span>
            <span className="flex items-center gap-1" aria-label={`${comments_count || 0} comentários`}><MessageCircle className={tamanho} />{comments_count || 0}</span>
        </span>
    );

    // ---------------------------------------------------------------- compacto
    if (compact) {
        return (
            <Link
                to={`/oferta/${deal_id}`}
                onClick={trackDetailClick}
                ref={refImpressao}
                className="sv-card sv-card--row group flex flex-row gap-3 p-2.5"
                data-testid={`deal-card-compact-${deal_id}`}
            >
                <div className="sv-card-media !m-0 relative w-16 h-16 flex-shrink-0 flex items-center justify-center">
                    <img src={imgSrc} alt={title} className="w-full h-full object-contain p-1" loading="lazy" onError={() => setImgError(true)} />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                    {loja}
                    <h3 className="text-[13px] font-semibold line-clamp-2 leading-snug" style={{ color: 'var(--text)' }}>{title}</h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        {semPreco
                            ? semPrecoCta('text-[12px]')
                            : <span className="text-sm font-extrabold sv-num" style={{ color: 'var(--text)' }}>{formatPrice(current_price)}</span>}
                        {temDesconto && <span className="sv-off !text-[10px]">-{discount_percent}%</span>}
                    </div>
                </div>
            </Link>
        );
    }

    // ------------------------------------------------------- card vertical
    // Link "esticado": a raiz é um <article>, o link de verdade fica no título e
    // cobre o card inteiro por uma camada ::after. Loja e compartilhar ficam por
    // cima dessa camada. Antes o card inteiro era um <a> com outro <a> e um
    // <button> dentro, o que é HTML inválido e confunde teclado e leitor de tela.
    const cardVertical = (classeExtra, testId) => (
        <article
            ref={refImpressao}
            className={`sv-card group h-full flex-col focus-within:ring-2 focus-within:ring-[var(--gold-2)] focus-within:ring-offset-2 focus-within:ring-offset-[var(--bg)] ${classeExtra}`}
            data-testid={testId}
        >
            <div className="sv-card-media aspect-square flex items-center justify-center">
                <img
                    src={imgSrc}
                    alt=""
                    className="w-full h-full object-contain p-3"
                    loading="lazy"
                    onError={() => setImgError(true)}
                />
                {temDesconto && (
                    <span className="sv-off absolute top-2 left-2 !text-[13px] !px-2 !py-1.5">-{discount_percent}%</span>
                )}
                <button
                    type="button"
                    onClick={handleShare}
                    className="sv-card-share absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center"
                    aria-label="Compartilhar oferta"
                    title="Compartilhar oferta"
                    data-testid={`deal-share-${deal_id}`}
                >
                    <Share2 className="w-3.5 h-3.5" />
                </button>
                {coupon_code && (
                    <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md" style={{ background: 'var(--bg-deep)', color: 'var(--sv-action)' }}>
                        <TicketPercent className="w-3.5 h-3.5" />Cupom
                    </span>
                )}
            </div>

            <div className="flex flex-col flex-1 px-3 pb-3 pt-1 gap-2">
                <div className="flex items-center justify-between gap-2 min-h-[20px]">
                    <span className="flex items-center gap-1.5 min-w-0">
                        <Link
                            to={`/loja/${encodeURIComponent((store_name || '').toLowerCase().replace(/\s+/g, '-'))}`}
                            className="sv-card-loja relative z-10 min-w-0 hover:underline"
                        >
                            {loja}
                        </Link>
                        {selosEstado}
                        {seloPrime}
                    </span>
                    {is_hot && (
                        <span className="flex items-center gap-0.5 text-[10px] font-extrabold px-1.5 py-0.5 rounded-md flex-shrink-0" style={{ background: 'rgba(255,90,112,0.16)', color: 'var(--sv-discount)' }}>
                            <Flame className="w-3 h-3" />HOT
                        </span>
                    )}
                </div>

                <h3 className="sv-card-title text-[14px] font-semibold leading-snug line-clamp-2 min-h-[38px]" style={{ color: 'var(--text)' }}>
                    <Link
                        to={`/oferta/${deal_id}`}
                        onClick={trackDetailClick}
                        className="outline-none after:absolute after:inset-0 after:content-[''] after:rounded-[inherit]"
                    >
                        {title}
                    </Link>
                </h3>

                <div className="flex items-baseline gap-2 flex-wrap mt-auto">
                    {semPreco
                        ? semPrecoCta('text-[15px]', 'card')
                        : <span className="text-[19px] font-extrabold tracking-tight sv-num" style={{ color: 'var(--text)' }}>{formatPrice(current_price)}</span>}
                    {temPrecoAntigo && (
                        <span className="text-[12px] line-through sv-num" style={{ color: 'var(--muted-2)' }}>{formatPrice(precoAntigo)}</span>
                    )}
                </div>

                {/* No trilho mobile o card tem 172 px: autor e contadores não cabem sem
                    truncar, então essa linha aparece a partir de `sm`. */}
                <div className={`${variant === 'tile' ? 'hidden sm:flex' : 'flex'} items-center justify-between gap-2 text-[11px]`} style={{ color: 'var(--muted-2)' }}>
                    <span className="flex items-center gap-1.5 min-w-0">
                        <span className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-[10px] font-bold" style={{ background: 'var(--surface-3)', color: 'var(--text)' }}>
                            {author_picture
                                ? <img src={resolveImageUrl(author_picture)} alt="" className="w-full h-full object-cover" />
                                : author_name?.charAt(0).toUpperCase()}
                        </span>
                        <span className="truncate">
                            <span style={{ color: 'var(--muted)' }}>{author_name?.split(' ')[0]}</span>
                            {timeAgo && <span>{author_name ? ', ' : ''}há {timeAgo}</span>}
                        </span>
                    </span>
                    {estatisticas()}
                </div>

            </div>
        </article>
    );

    // ------------------------------------------------ linha de lista
    // Usada em seções de lista compacta. Aqui o botão de ação faz sentido: a linha
    // tem espaço lateral e a lista é curta.
    if (variant === 'row') {
        return (
            <article ref={refImpressao} className="sv-card sv-card--row group flex flex-row items-center gap-3 p-2.5 pr-3 focus-within:ring-2 focus-within:ring-[var(--gold-2)]"
                data-testid={`deal-card-row-${deal_id}`}>
                <div className="sv-card-media !m-0 w-[84px] h-[84px] sm:w-[92px] sm:h-[92px] shrink-0 flex items-center justify-center">
                    <img src={imgSrc} alt="" className="w-full h-full object-contain p-1.5" loading="lazy" onError={() => setImgError(true)} />
                    {temDesconto && <span className="sv-off absolute top-1 left-1 !text-[11px]">-{discount_percent}%</span>}
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                    {loja}
                    <h3 className="sv-card-title text-[14px] font-semibold line-clamp-2 leading-snug" style={{ color: 'var(--text)' }}>
                        <Link to={`/oferta/${deal_id}`} onClick={trackDetailClick}
                            className="outline-none after:absolute after:inset-0 after:content-[''] after:rounded-[inherit]">
                            {title}
                        </Link>
                    </h3>
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                        {semPreco
                            ? semPrecoCta('text-[14px]', 'card_row')
                            : <span className="text-[17px] font-extrabold sv-num" style={{ color: 'var(--text)' }}>{formatPrice(current_price)}</span>}
                        {temPrecoAntigo && <span className="text-[12px] line-through sv-num" style={{ color: 'var(--muted-2)' }}>{formatPrice(precoAntigo)}</span>}
                        {seloPrime}
                    </div>
                </div>
                <span className="hidden sm:inline-flex sv-btn-action !px-3.5 !py-2 !text-[13px] shrink-0" aria-hidden="true">Ver oferta</span>
            </article>
        );
    }

    if (variant === 'tile') {
        return cardVertical('flex', `deal-card-${deal_id}`);
    }

    // ------------------------------------------- padrão: linha no mobile
    return (
        <>
            <Link
                to={`/oferta/${deal_id}`}
                onClick={trackDetailClick}
                ref={refImpressao}
                className="sm:hidden sv-card sv-card--row flex flex-row overflow-hidden"
                data-testid={`deal-card-mobile-${deal_id}`}
            >
                <div className="sv-card-media !my-2 !ml-2 !mr-0 w-[112px] flex-shrink-0 flex items-center justify-center">
                    {/* Altura fixa: com min 112 / max 128 a foto nascia com 112 px e crescia ao
                        carregar, empurrando a lista 13 px a cada card (layout shift medido). */}
                    <img src={imgSrc} alt={title} className="w-full object-contain p-2" style={{ height: 128 }} loading="lazy" onError={() => setImgError(true)} />
                    {temDesconto && <span className="sv-off absolute top-1.5 left-1.5 !text-[11px]">-{discount_percent}%</span>}
                </div>
                <div className="flex-1 flex flex-col justify-between p-3 min-w-0 gap-1">
                    <div className="flex items-center justify-between gap-1">
                        {loja}
                        {is_hot && <span className="text-[10px] font-extrabold px-1 rounded" style={{ background: 'rgba(255,90,112,0.16)', color: 'var(--sv-discount)' }}>HOT</span>}
                    </div>
                    <h3 className="text-[13px] font-semibold line-clamp-2 leading-snug" style={{ color: 'var(--text)', wordBreak: 'break-word' }}>{title}</h3>
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                        {semPreco
                            ? semPrecoCta('text-[13px]')
                            : <span className="text-[16px] font-extrabold sv-num" style={{ color: 'var(--text)' }}>{formatPrice(current_price)}</span>}
                        {temPrecoAntigo && <span className="text-[11px] line-through sv-num" style={{ color: 'var(--muted-2)' }}>{formatPrice(precoAntigo)}</span>}
                        {seloPrime}
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] truncate" style={{ color: 'var(--muted-2)' }}>
                            {author_name?.split(' ')[0]}{timeAgo ? `${author_name ? ', ' : ''}há ${timeAgo}` : ''}
                        </span>
                        {estatisticas('w-3 h-3')}
                    </div>
                </div>
            </Link>

            {cardVertical('hidden sm:flex', `deal-card-${deal_id}`)}
        </>
    );
};
